import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw, Bug } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((props: { error: Error | null; reset: () => void }) => ReactNode);
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  buildReportHref = () => {
    const supportEmail = (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) ?? "";
    const subject = "Alea ERP — Bug Report";
    const body = [
      "Describe what you were doing when the error occurred:",
      "",
      "",
      "--- technical details (please keep) ---",
      `Time: ${new Date().toISOString()}`,
      `Page: ${window.location.href}`,
      `Error: ${this.state.error?.message ?? "Unknown error"}`,
      this.state.componentStack ? `Where: ${this.state.componentStack.trim().split("\n")[0]?.trim()}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    return `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      if (typeof this.props.fallback === "function") {
        return this.props.fallback({ error: this.state.error, reset: this.handleReset });
      }
      return this.props.fallback;
    }

    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f6f8] p-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-2xl shadow-lg border border-border p-8 text-center">
            <div className="flex justify-center mb-5">
              <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="h-7 w-7 text-red-500" />
              </div>
            </div>

            <h1 className="text-xl font-bold text-foreground mb-2">
              Something went wrong
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              An unexpected error occurred. Your data is safe — refreshing the page
              will usually fix this.
            </p>

            {this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-muted-foreground transition-colors">
                  Show technical details
                </summary>
                <pre className="mt-2 p-3 bg-muted/50 border border-border rounded-lg text-xs text-muted-foreground overflow-auto max-h-32 whitespace-pre-wrap">
                  {this.state.error.message}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReload} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Reload page
              </Button>
              <Button variant="outline" onClick={() => (window.location.href = "/dashboard")}>
                Go to dashboard
              </Button>
            </div>

            <p className="mt-6 text-xs text-muted-foreground">
              If this keeps happening, contact your administrator or{" "}
              <a
                href={this.buildReportHref()}
                className="inline-flex items-center gap-1 font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground/80 transition-colors"
              >
                <Bug className="h-3 w-3" />
                report this problem
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }
}
